/**
 * Question Phase page — /attempt/:id
 *
 * Stories covered:
 *   19  — visible countdown timer
 *   20  — visual warning below 5 minutes
 *   21  — seconds display in final 60 seconds
 *   22  — autosave on every answer change (debounced ≤2s)
 *   23  — free navigation between questions
 *   24  — "N of M answered" progress indicator
 *   25  — Section headings in navigator
 *   26  — manual submit button
 *   27  — auto-submit on timer expiry
 *   29  — unanswered-question warning before submit
 *   29a — full keyboard navigation
 *   29b — ARIA live region timer at thresholds
 *   29d — axe-core target: no WCAG 2.1 AA violations
 *
 * Timer semantics:
 *   - On mount, polls GET /api/attempts/:id/timer for `seconds_remaining`.
 *   - Client counts down locally between polls (polls every 30s).
 *   - When seconds_remaining ≤ 0, auto-submits with submitter='timer'.
 *   - Client NEVER trusts its own local clock as authoritative; server value
 *     overrides on every poll.
 *
 * Autosave:
 *   - Debounced 1.5s after last keystroke/click.
 *   - Idempotency key = `${attemptId}:${questionId}:${Date.now()}` (per keystroke
 *     session — a new key on each edit burst, consistent within the same
 *     debounce window).
 *   - Status shown in the header: "Saved" / "Saving…" / "Save failed".
 *
 * Accessibility (AC 6, 7, 8):
 *   - All interactive elements have visible focus indicator (outline-2).
 *   - Timer wrapped in `role="timer" aria-live="polite"` (upgrades to
 *     `aria-live="assertive"` in the final minute).
 *   - Separate ARIA announcer for key thresholds (10min, 5min, 1min, 30s).
 *   - Arrow keys navigate the question list; Space/Enter select options.
 *   - Ordering questions have Move-up (Alt+Up) / Move-down (Alt+Down) keyboard
 *     shortcuts on focused items.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
  type RefObject,
} from 'react';
import './question-phase.css';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Input } from '@/components/ui';
import {
  flagQuestion,
  getTimer,
  recordAnswer,
  submitAttempt,
  type AttemptResultView,
} from '../../state/attempt-engine/api.ts';
import { useSecondTabDetection } from '../../hooks/useSecondTabDetection.ts';
import { useOfflineQueue } from '../../hooks/useOfflineQueue.ts';

const API_BASE = '';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface Option {
  id: string;
  text: string;
  sort_order: number;
}

interface Question {
  id: string;
  type: string;
  text: string;
  image_url: string | null;
  section_name: string | null;
  options?: Option[];
  current_answer?: string | null;
  flagged?: boolean;
}

interface Section {
  name: string | null;
  question_ids: string[];
}

interface AttemptData {
  attempt_id: string;
  test: {
    id: string;
    title: string;
    pass_mark: number;
    show_result: boolean;
    sections: Section[];
  };
  questions: Question[];
  effective_time_limit_seconds: number;
  timer_started_at: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Timer utilities
// ─────────────────────────────────────────────────────────────────────────────

function formatTimer(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${String(rem).padStart(2, '0')}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Autosave debounce hook
// ─────────────────────────────────────────────────────────────────────────────

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

function useAutosave(attemptId: string) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

  const save = useCallback(
    (questionId: string, answer: string, idempotencyKey: string) => {
      setSaveStatus('saving');
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        void (async () => {
          const r = await recordAnswer(API_BASE, attemptId, {
            question_id: questionId,
            answer,
            idempotency_key: idempotencyKey,
          });
          setSaveStatus(r.ok ? 'saved' : 'error');
        })();
      }, 1500);
    },
    [attemptId],
  );

  return { save, saveStatus };
}

// ─────────────────────────────────────────────────────────────────────────────
// Timer hook
// ─────────────────────────────────────────────────────────────────────────────

function useServerTimer(
  attemptId: string,
  onExpire: () => void,
): { secondsRemaining: number; isWarning: boolean; isCritical: boolean; ariaLiveRef: RefObject<HTMLDivElement> } {
  const [secondsRemaining, setSecondsRemaining] = useState<number>(0);
  const localTickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const announcerRef = useRef<Set<number>>(new Set([600, 300, 60, 30])); // minutes*60 thresholds

  const ariaLiveRef = useRef<HTMLDivElement>(null);

  // Story 29b — announce at thresholds.
  function announce(message: string, assertive: boolean) {
    if (!ariaLiveRef.current) return;
    ariaLiveRef.current.setAttribute('aria-live', assertive ? 'assertive' : 'polite');
    ariaLiveRef.current.textContent = message;
    // Clear after 3s to allow re-announcement at same threshold on resume.
    setTimeout(() => {
      if (ariaLiveRef.current) ariaLiveRef.current.textContent = '';
    }, 3000);
  }

  useEffect(() => {
    let cancelled = false;

    async function poll() {
      const r = await getTimer(API_BASE, attemptId);
      if (cancelled || !r.ok) return;
      setSecondsRemaining(r.data.seconds_remaining);
    }

    void poll();

    // Poll every 30s to resync with server.
    const pollInterval = setInterval(() => { void poll(); }, 30_000);
    // Local countdown tick every 1s.
    localTickRef.current = setInterval(() => {
      setSecondsRemaining((prev) => {
        const next = Math.max(0, prev - 1);
        // Check announcement thresholds.
        const thresholds = announcerRef.current;
        if (thresholds.has(next) && next > 0) {
          thresholds.delete(next);
          const minutes = Math.floor(next / 60);
          const isAssertive = next <= 60;
          const msg =
            next <= 60
              ? `${next} seconds remaining`
              : `${minutes} minutes remaining`;
          announce(msg, isAssertive);
        }
        if (next === 0) {
          onExpire();
        }
        return next;
      });
    }, 1000);

    return () => {
      cancelled = true;
      clearInterval(pollInterval);
      if (localTickRef.current) clearInterval(localTickRef.current);
    };
  }, [attemptId, onExpire]);

  return {
    secondsRemaining,
    isWarning: secondsRemaining <= 300 && secondsRemaining > 0, // < 5 min
    isCritical: secondsRemaining <= 60 && secondsRemaining > 0, // < 1 min (seconds display)
    ariaLiveRef, // Story 29b fix: caller attaches this ref to the hidden ARIA announcer div
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Answer input components per QuestionType
// ─────────────────────────────────────────────────────────────────────────────

interface AnswerInputProps {
  question: Question;
  currentAnswer: string;
  onChange: (answer: string) => void;
}

function McqInput({ question, currentAnswer, onChange }: AnswerInputProps): ReactElement {
  return (
    <fieldset>
      <legend className="sr-only">Choose one answer</legend>
      {(question.options ?? []).map((opt) => (
        <label
          key={opt.id}
          style={{
            display: 'block',
            padding: '8px 12px',
            marginBottom: 4,
            border: '1px solid #ccc',
            borderRadius: 4,
            cursor: 'pointer',
            background: currentAnswer === opt.id ? '#e3f2fd' : 'transparent',
          }}
        >
          <input
            type="radio"
            name={question.id}
            value={opt.id}
            checked={currentAnswer === opt.id}
            onChange={() => onChange(opt.id)}
            aria-label={opt.text}
            style={{ marginRight: 8 }}
          />
          {opt.text}
        </label>
      ))}
    </fieldset>
  );
}

function MultiInput({ question, currentAnswer, onChange }: AnswerInputProps): ReactElement {
  const selected = new Set(currentAnswer ? currentAnswer.split(',') : []);
  function toggle(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next].sort().join(','));
  }
  return (
    <fieldset>
      <legend className="sr-only">Select all that apply</legend>
      {(question.options ?? []).map((opt) => (
        <label
          key={opt.id}
          style={{
            display: 'block',
            padding: '8px 12px',
            marginBottom: 4,
            border: '1px solid #ccc',
            borderRadius: 4,
            cursor: 'pointer',
            background: selected.has(opt.id) ? '#e3f2fd' : 'transparent',
          }}
        >
          <input
            type="checkbox"
            value={opt.id}
            checked={selected.has(opt.id)}
            onChange={() => toggle(opt.id)}
            aria-label={opt.text}
            style={{ marginRight: 8 }}
          />
          {opt.text}
        </label>
      ))}
    </fieldset>
  );
}

function OrderInput({ question, currentAnswer, onChange }: AnswerInputProps): ReactElement {
  const initialOrder = currentAnswer
    ? currentAnswer.split(',')
    : (question.options ?? []).slice().sort((a, b) => a.sort_order - b.sort_order).map((o) => o.id);

  const [order, setOrder] = useState<string[]>(initialOrder);

  function move(index: number, direction: -1 | 1) {
    const next = [...order];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target]!, next[index]!];
    setOrder(next);
    onChange(next.join(','));
  }

  // Story 29a — up/down keyboard shortcuts on focused item.
  function handleKeyDown(e: KeyboardEvent<HTMLLIElement>, index: number) {
    if (e.altKey && e.key === 'ArrowUp') { e.preventDefault(); move(index, -1); }
    if (e.altKey && e.key === 'ArrowDown') { e.preventDefault(); move(index, 1); }
  }

  const optionMap = useMemo(() => {
    const m: Record<string, string> = {};
    for (const o of question.options ?? []) m[o.id] = o.text;
    return m;
  }, [question.options]);

  return (
    <ol aria-label="Reorder items using Alt+Up and Alt+Down" style={{ paddingLeft: 20 }}>
      {order.map((id, index) => (
        <li
          key={id}
          tabIndex={0}
          onKeyDown={(e) => handleKeyDown(e, index)}
          style={{
            padding: '8px 12px',
            marginBottom: 4,
            border: '1px solid #ccc',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'grab',
          }}
          aria-label={`${optionMap[id] ?? id}. Press Alt+Up or Alt+Down to reorder.`}
        >
          <span style={{ flex: 1 }}>{optionMap[id] ?? id}</span>
          <Button variant="ghost" size="sm" type="button" aria-label={`Move up: ${optionMap[id] ?? id}`} onClick={() => move(index, -1)} disabled={index === 0}>
            ↑
          </Button>
          <Button variant="ghost" size="sm" type="button" aria-label={`Move down: ${optionMap[id] ?? id}`} onClick={() => move(index, 1)} disabled={index === order.length - 1}>
            ↓
          </Button>
        </li>
      ))}
    </ol>
  );
}

function TextInput({ question, currentAnswer, onChange }: AnswerInputProps): ReactElement {
  return (
    <Input
      type={question.type === 'fillnumber' ? 'number' : 'text'}
      value={currentAnswer}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Your answer"
      style={{
        width: '100%',
        padding: '8px 12px',
        border: '1px solid #ccc',
        borderRadius: 4,
        fontSize: 16,
      }}
    />
  );
}

function AnswerInput(props: AnswerInputProps): ReactElement {
  const { type } = props.question;
  if (type === 'multiple' || type === 'truefalse') return <McqInput {...props} />;
  if (type === 'multi') return <MultiInput {...props} />;
  if (type === 'order') return <OrderInput {...props} />;
  return <TextInput {...props} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

export default function QuestionPhasePage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Attempt data is passed via navigation state from the pre-screen page.
  const [attempt, setAttempt] = useState<AttemptData | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [flagged, setFlagged] = useState<Record<string, boolean>>({});
  const [activeIndex, setActiveIndex] = useState(0);
  const [showSubmitWarning, setShowSubmitWarning] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [_result, setResult] = useState<AttemptResultView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Idempotency key per edit burst (changes each time the user starts editing).
  const idempotencyBurstRef = useRef<string>(`${id ?? ''}:${Date.now()}`);

  const { save, saveStatus } = useAutosave(id ?? '');

  const handleExpire = useCallback(() => {
    if (!id || submitted) return;
    void (async () => {
      await submitAttempt(API_BASE, id, 'timer');
      setSubmitted(true);
      // Navigate to result.
      navigate(`/attempts/${id}/result`, { replace: true });
    })();
  }, [id, submitted, navigate]);

  const { secondsRemaining, isWarning, isCritical, ariaLiveRef } = useServerTimer(
    id ?? '',
    handleExpire,
  );

  // AC4 — second-tab detection: blocks this tab if another tab already has this attempt open.
  const { isSecondTab } = useSecondTabDetection(id ?? '');

  // AC7 — offline queue: queues failed mutations to localStorage; flushes on reconnect.
  useOfflineQueue();

  // Load attempt data from navigation state or sessionStorage fallback.
  useEffect(() => {
    const key = `attempt:${id ?? ''}`;
    const stored = sessionStorage.getItem(key);
    if (stored) {
      let data: AttemptData | null = null;
      try {
        data = JSON.parse(stored) as AttemptData;
      } catch {
        // Corrupted or legacy storage value — discard it so the page can
        // render the "Attempt data not found" fallback instead of crashing.
        sessionStorage.removeItem(key);
      }
      if (data) {
        setAttempt(data);
        // Hydrate existing answers from server response.
        const hydrated: Record<string, string> = {};
        for (const q of data.questions) {
          if (q.current_answer) hydrated[q.id] = q.current_answer;
        }
        setAnswers(hydrated);
      }
    }
    setLoading(false);
  }, [id]);

  const questions = attempt?.questions ?? [];
  const answeredCount = Object.keys(answers).filter((k) => answers[k] !== '').length;
  const activeQuestion = questions[activeIndex];

  function handleAnswerChange(questionId: string, answer: string) {
    setAnswers((prev) => ({ ...prev, [questionId]: answer }));
    // Rotate idempotency key on change burst.
    idempotencyBurstRef.current = `${questionId}:${Date.now()}`;
    save(questionId, answer, idempotencyBurstRef.current);
  }

  async function handleFlagToggle(questionId: string) {
    if (!id) return;
    const next = !flagged[questionId];
    setFlagged((prev) => ({ ...prev, [questionId]: next }));
    await flagQuestion(API_BASE, id, questionId, next);
  }

  function handleSubmitClick() {
    const unanswered = questions.length - answeredCount;
    if (unanswered > 0) {
      setShowSubmitWarning(true);
      return;
    }
    void doSubmit();
  }

  async function doSubmit() {
    if (!id) return;
    setShowSubmitWarning(false);
    const r = await submitAttempt(API_BASE, id, 'candidate');
    if (r.ok) {
      setResult(r.data);
      setSubmitted(true);
      navigate(`/attempts/${id}/result`, { replace: true });
    } else {
      setError(`Submit failed: ${r.error.message}`);
    }
  }

  // Story 29a — arrow key navigation between questions.
  function handleNavKeyDown(e: KeyboardEvent<HTMLElement>) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, questions.length - 1));
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    }
  }

  // AC4 — block this tab if another tab already owns this attempt (J-X9 § Scenario 3).
  // Render BEFORE the loading check so the block is immediate even while data loads.
  if (isSecondTab) {
    return (
      <div
        role="alert"
        aria-live="assertive"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          fontFamily: 'system-ui, sans-serif',
          padding: 32,
          textAlign: 'center',
        }}
      >
        <div>
          <h1 style={{ fontSize: 24, marginBottom: 12 }}>Attempt open in another tab</h1>
          <p style={{ color: '#555' }}>
            This attempt is already active in another browser tab. Close the other tab to continue
            here, or return to it.
          </p>
        </div>
      </div>
    );
  }

  if (loading) return <div role="status" aria-live="polite">Loading question phase…</div>;
  if (error) return <div role="alert">{error}</div>;
  if (!attempt) {
    return (
      <div role="alert">
        <p>Attempt data not found. Please return to the start link.</p>
      </div>
    );
  }

  return (
    <div className="qp-shell" data-testid="question-phase">
      {/* ── ARIA live region for timer thresholds (Story 29b) ── */}
      {/* Story 29b fix: ref={ariaLiveRef} connects this element to useServerTimer's
          announce() function. Without this ref, ariaLiveRef.current is always null
          and all threshold announcements silently no-op. */}
      <div
        ref={ariaLiveRef}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        id="timer-announcer"
      />

      {/* ── Sticky top bar: timer always visible (J-X24 requirement) ── */}
      <header className="qp-topbar" data-testid="qp-topbar">
        {/* Stories 19-21 — timer (sticky top, always visible per J-X24) */}
        <div
          role="timer"
          aria-live={isCritical ? 'assertive' : 'polite'}
          aria-label={`Time remaining: ${formatTimer(secondsRemaining)}`}
          className={`qp-timer${isWarning ? ' qp-timer--warning' : ''}${isCritical ? ' qp-timer--critical' : ''}`}
          data-testid="qp-timer"
        >
          {formatTimer(secondsRemaining)}
          {isCritical && (
            <span className="qp-timer-sub">
              sec left
            </span>
          )}
        </div>

        <h1 className="qp-title">{attempt.test.title}</h1>

        <div className="qp-topbar-right">
          {/* Story 24 — progress indicator */}
          <span aria-label={`${answeredCount} of ${questions.length} answered`} className="qp-progress">
            {answeredCount}/{questions.length}
          </span>

          {/* Autosave status */}
          <span
            aria-live="polite"
            className={`qp-save${saveStatus === 'error' ? ' qp-save--error' : ''}`}
          >
            {saveStatus === 'saving' && 'Saving…'}
            {saveStatus === 'saved' && 'Saved'}
            {saveStatus === 'error' && 'Save failed'}
          </span>

          {/* Submit button (Story 26) */}
          <Button
            variant="primary"
            type="button"
            onClick={handleSubmitClick}
            disabled={submitted}
            data-testid="submit-button"
          >
            Submit
          </Button>
        </div>
      </header>

      {/* ── Body: navigator + question pane ── */}
      <div className="qp-body">
        {/* ── Navigator sidebar (Stories 23, 25) ── */}
        <nav
          aria-label="Question navigator"
          className="qp-nav"
          onKeyDown={handleNavKeyDown}
          data-testid="question-navigator"
        >
          {/* Section-grouped navigator (Story 25) */}
          {attempt.test.sections.map((section) => (
            <div key={section.name ?? '__null'}>
              {section.name && (
                <h3 className="qp-nav-section">
                  {section.name}
                </h3>
              )}
              {section.question_ids.map((qId) => {
                const idx = questions.findIndex((q) => q.id === qId);
                const q = questions[idx];
                if (!q) return null;
                const isAnswered = Boolean(answers[qId] && answers[qId] !== '');
                const isFlagged = flagged[qId];
                const isActiveQ = idx === activeIndex;
                return (
                  <Button
                    key={qId}
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={() => setActiveIndex(idx)}
                    aria-current={isActiveQ ? 'true' : undefined}
                    aria-label={`Question ${idx + 1}${isAnswered ? ', answered' : ', unanswered'}${isFlagged ? ', flagged' : ''}`}
                    className={isActiveQ ? 'border-accent bg-accent/10' : isAnswered ? 'border-success/30 bg-success/5' : ''}
                  >
                    {idx + 1}. {isAnswered ? '✓ ' : ''}{isFlagged ? '🚩 ' : ''}
                    {q.text.slice(0, 30)}{q.text.length > 30 ? '…' : ''}
                  </Button>
                );
              })}
            </div>
          ))}
        </nav>

        {/* ── Main content ── */}
        <main className="qp-main">
          {/* ── Unanswered warning dialog (Story 29) ── */}
          {showSubmitWarning && (
            <div
              role="dialog"
              aria-labelledby="submit-warning-title"
              aria-modal="true"
              className="qp-dialog-backdrop"
            >
              <div className="qp-dialog">
                <h2 id="submit-warning-title" className="qp-dialog-title">
                  Submit with {questions.length - answeredCount} unanswered question
                  {questions.length - answeredCount === 1 ? '' : 's'}?
                </h2>
                <p className="qp-dialog-body">
                  You have {questions.length - answeredCount} unanswered question
                  {questions.length - answeredCount === 1 ? '' : 's'}. Are you sure you want to submit?
                </p>
                <div className="qp-dialog-actions">
                  <Button variant="secondary" type="button" onClick={() => setShowSubmitWarning(false)} autoFocus>
                    Continue answering
                  </Button>
                  <Button variant="danger" type="button" onClick={() => void doSubmit()}>
                    Submit anyway
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* ── Active question pane ── */}
          {activeQuestion && (
            <section
              className="qp-question"
              aria-labelledby={`question-${activeQuestion.id}-title`}
            >
              {/* Question header */}
              <div className="qp-question-header">
                <h2
                  id={`question-${activeQuestion.id}-title`}
                  className="qp-question-text"
                >
                  <span className="qp-question-num">Q{activeIndex + 1}.</span>
                  {activeQuestion.text}
                </h2>

                {/* Flag button — min 44px tap target */}
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  aria-pressed={flagged[activeQuestion.id] ?? false}
                  aria-label={flagged[activeQuestion.id] ? 'Unflag this question' : 'Flag for review'}
                  onClick={() => void handleFlagToggle(activeQuestion.id)}
                  className={flagged[activeQuestion.id] ? 'border-warning bg-warning/10' : ''}
                >
                  {flagged[activeQuestion.id] ? '🚩 Flagged' : '⚑ Flag'}
                </Button>
              </div>

              {/* Optional image — full-width on mobile with pinch-zoom (J-X24) */}
              {activeQuestion.image_url && (
                <img
                  src={activeQuestion.image_url}
                  alt="Question image"
                  className="qp-question-image"
                  data-testid="question-image"
                />
              )}

              {/* Answer input */}
              <AnswerInput
                question={activeQuestion}
                currentAnswer={answers[activeQuestion.id] ?? ''}
                onChange={(answer) => handleAnswerChange(activeQuestion.id, answer)}
              />

              {/* Navigation buttons (Story 23) */}
              <div className="qp-nav-buttons">
                <Button variant="secondary" type="button" onClick={() => setActiveIndex((i) => Math.max(0, i - 1))} disabled={activeIndex === 0} aria-label="Previous question">
                  ← Prev
                </Button>
                <Button variant="secondary" type="button" onClick={() => setActiveIndex((i) => Math.min(questions.length - 1, i + 1))} disabled={activeIndex === questions.length - 1} aria-label="Next question">
                  Next →
                </Button>
              </div>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}
